/**
 * Dynamic SEO Generator
 *
 * Generates SEO metadata dynamically from cache data and configuration.
 * Reduces reliance on static JSON and leverages existing data sources.
 */

import {
  CachedContent,
  Country,
  GlobalRegion,
  Plan,
  Region,
} from '../../../interfaces/cached-content';
import { SchemaMarkup, SeoMeta } from '../../../interfaces/seo';
import { IMAGE_DIMENSIONS, SEO_DEFAULTS } from './seo-defaults.config';

export interface SeoGeneratorConfig {
  customerName: string;
  baseUrl: string;
  cdnUrl?: string;
  twitterHandle?: string;
  logoUrl?: string;
  contactEmail?: string;
  currency: string;
}

/**
 * Extracts SEO configuration from cache content
 */
export function extractSeoConfig(cacheContent: CachedContent): SeoGeneratorConfig {
  const webAppConfig = cacheContent.cache.config.webAppConfig || {};
  const contactDetails = cacheContent.cache.config.contactDetails;
  const defaultCurrency = cacheContent.cache.config.currencyObj?.defaultCurrency?.currency || 'USD';

  // Extract Twitter handle from full URL if present
  const twitterUrl = contactDetails?.social?.twitter || contactDetails?.social?.social?.twitter;
  const twitterHandle = twitterUrl ? extractTwitterHandle(twitterUrl) : undefined;

  return {
    customerName: webAppConfig.name || '',
    baseUrl: webAppConfig.domainUrl || '',
    cdnUrl: webAppConfig.s3BucketAssets || webAppConfig.domainUrl || '',
    twitterHandle,
    logoUrl: webAppConfig.logo || '',
    contactEmail: contactDetails?.emailAddress || '',
    currency: defaultCurrency,
  };
}

/**
 * Extracts Twitter handle from a full Twitter URL
 */
function extractTwitterHandle(url: string): string | undefined {
  if (!url) {
    return undefined;
  }

  const parts = url.split('/');
  const handle = parts.at(-1);

  return handle ? `@${handle.replace('@', '')}` : undefined;
}

/**
 * Generates canonical URL from base URL and path
 */
export function generateCanonicalUrl(baseUrl: string, path: string): string {
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBaseUrl}${cleanPath}`;
}

/**
 * Generates OG image URL based on pattern
 */
export function generateOgImageUrl(
  config: SeoGeneratorConfig,
  pageName: string,
  customPath?: string
): string | undefined {
  if (customPath) {
    return customPath;
  }

  if (!SEO_DEFAULTS.imagePatterns.og) {
    return undefined;
  }

  const baseUrl = config.cdnUrl || config.baseUrl;
  return `${baseUrl}${SEO_DEFAULTS.imagePatterns.og.replace('${page}', pageName)}`;
}

/**
 * Generates Twitter image URL based on pattern
 */
export function generateTwitterImageUrl(
  config: SeoGeneratorConfig,
  pageName: string,
  customPath?: string
): string | undefined {
  if (customPath) {
    return customPath;
  }

  if (!SEO_DEFAULTS.imagePatterns.twitter) {
    return undefined;
  }

  const baseUrl = config.cdnUrl || config.baseUrl;
  return `${baseUrl}${SEO_DEFAULTS.imagePatterns.twitter.replace('${page}', pageName)}`;
}

/**
 * Applies default SEO values to a SeoMeta object
 * Only adds values that are explicitly provided or can be generated from customer data
 * No hardcoded fallbacks for white-label applications
 */
export function applyDefaults(
  seoMeta: Partial<SeoMeta>,
  config: SeoGeneratorConfig,
  pageName: string,
  currentPath: string
): SeoMeta {
  // Preserve title if already set (may already have variables replaced)
  // Only use customer name fallback if title is completely missing
  const title = seoMeta.title || (config.customerName ? `${config.customerName}` : '');
  const description = seoMeta.description || '';

  return {
    // Basic tags - only if provided
    title,
    description,
    keywords: seoMeta.keywords || '',

    // Open Graph - only add if values exist
    ogTitle: seoMeta.ogTitle || title || undefined,
    ogDescription: seoMeta.ogDescription || description || undefined,
    ogImage: seoMeta.ogImage || generateOgImageUrl(config, pageName),
    ogImageWidth: seoMeta.ogImage ? seoMeta.ogImageWidth || IMAGE_DIMENSIONS.og.width : undefined,
    ogImageHeight: seoMeta.ogImage
      ? seoMeta.ogImageHeight || IMAGE_DIMENSIONS.og.height
      : undefined,
    ogUrl:
      seoMeta.ogUrl ||
      (config.baseUrl ? generateCanonicalUrl(config.baseUrl, currentPath) : undefined),
    ogType: seoMeta.ogType, // No default - only if specified
    ogSiteName: seoMeta.ogSiteName || (config.customerName ? config.customerName : undefined),

    // Twitter - only add if values exist
    twitterCard: seoMeta.twitterCard, // No default - only if specified
    twitterTitle: seoMeta.twitterTitle || seoMeta.ogTitle || title || undefined,
    twitterDescription:
      seoMeta.twitterDescription || seoMeta.ogDescription || description || undefined,
    twitterImage:
      seoMeta.twitterImage || seoMeta.ogImage || generateTwitterImageUrl(config, pageName),
    twitterSite: seoMeta.twitterSite || config.twitterHandle,
    twitterCreator: seoMeta.twitterCreator,

    // Additional
    canonical:
      seoMeta.canonical ||
      (config.baseUrl ? generateCanonicalUrl(config.baseUrl, currentPath) : undefined),
    robots: seoMeta.robots, // No default - only if specified
    author: seoMeta.author, // No default - only if specified

    // Schema markup
    schemaMarkup: seoMeta.schemaMarkup,
  };
}

/**
 * Replaces template variables in a string
 * Supports both ${variable} and {variable} syntax
 */
export function replaceTemplateVariables(
  template: string,
  variables: Record<string, string | number>
): string {
  let result = template;

  for (const [key, value] of Object.entries(variables)) {
    const dollarPlaceholder = `\${${key}}`;
    const bracePlaceholder = `{${key}}`;
    const stringValue = String(value);

    result = result.replaceAll(dollarPlaceholder, stringValue);
    result = result.replaceAll(bracePlaceholder, stringValue);
  }

  return result;
}

/**
 * Calculates minimum price from plans
 */
function calculateMinPrice(plans: Plan[] | undefined, currency: string): number {
  if (!plans || plans.length === 0) {
    return 0;
  }

  const prices = plans
    .map((plan) => plan.priceBundle?.[currency] ?? 0)
    .filter((price) => price > 0);

  if (prices.length === 0) {
    return 0;
  }

  return Math.min(...prices);
}

/**
 * Formats price for display
 */
function formatPrice(price: number, currency: string): string {
  if (price <= 0) {
    return 'affordable prices';
  }

  const currencySymbol = getCurrencySymbol(currency);
  return `${currencySymbol}${price}`;
}

/**
 * Gets currency symbol
 */
function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    INR: '₹',
  };

  return symbols[currency] || '$';
}

/**
 * Formats description with plan and price information
 */
function formatDescriptionWithPlanInfo(
  baseDescription: string,
  planCount: number,
  minPrice: number,
  currency: string
): string {
  if (planCount === 0) {
    return baseDescription;
  }

  const priceText = minPrice > 0 ? ` from ${formatPrice(minPrice, currency)}` : '';
  return `${baseDescription}${priceText}`;
}

/**
 * Creates structured data for product offers
 */
function createProductOffers(
  planCount: number,
  minPrice: number,
  currency: string
):
  | {
      '@type': string;
      priceCurrency: string;
      lowPrice: number;
      offerCount: number;
    }
  | undefined {
  if (planCount === 0 || minPrice <= 0) {
    return undefined;
  }

  return {
    '@type': 'AggregateOffer',
    priceCurrency: currency,
    lowPrice: minPrice,
    offerCount: planCount,
  };
}

/**
 * Generic function to generate location-based SEO
 */
interface LocationData {
  name: string;
  plans?: Plan[];
  supportedCountries?: string[];
}

function generateLocationSeo(
  config: SeoGeneratorConfig,
  location: LocationData,
  locationType: 'country' | 'region' | 'globalRegion',
  template?: Partial<SeoMeta>
): Partial<SeoMeta> {
  const planCount = location.plans?.length || 0;
  const countryCount = location.supportedCountries?.length || 0;
  const minPrice = calculateMinPrice(location.plans, config.currency);

  const variables: Record<string, string | number> = {
    locationName: location.name,
    planCount,
    customerName: config.customerName,
  };

  // Add location-specific variables
  if (locationType === 'country') {
    variables['countryName'] = location.name;
  } else if (locationType === 'region') {
    variables['regionName'] = location.name;
    variables['countryCount'] = countryCount;
  } else if (locationType === 'globalRegion') {
    variables['globalRegionName'] = location.name;
    variables['countryCount'] = countryCount;
  }

  // Add formatted price
  variables['minPrice'] = formatPrice(minPrice, config.currency);

  // Generate title
  const title = template?.title
    ? replaceTemplateVariables(template.title, variables)
    : generateDefaultTitle(location.name, locationType, config.customerName);

  // Generate description
  const description = template?.description
    ? replaceTemplateVariables(template.description, variables)
    : generateDefaultDescription(
        location.name,
        locationType,
        planCount,
        countryCount,
        minPrice,
        config.currency
      );

  // Generate keywords
  const keywords = template?.keywords
    ? replaceTemplateVariables(template.keywords, variables)
    : generateDefaultKeywords(location.name, locationType, config.customerName);

  // Generate OG and Twitter metadata
  const ogTitle = generateOgTitle(location.name, locationType, config.customerName);
  const ogDescription = generateOgDescription(location.name, locationType, planCount, countryCount);
  const twitterTitle = generateTwitterTitle(location.name, locationType);
  const twitterDescription = generateTwitterDescription(
    planCount,
    countryCount,
    minPrice,
    config.currency,
    location.name
  );

  // Generate schema markup
  const schemaMarkup =
    template?.schemaMarkup ||
    generateProductSchema(
      location.name,
      locationType,
      config.customerName,
      countryCount,
      planCount,
      minPrice,
      config.currency
    );

  return {
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    twitterTitle,
    twitterDescription,
    schemaMarkup,
  };
}

/**
 * Generates default title based on location type
 */
function generateDefaultTitle(
  locationName: string,
  locationType: string,
  customerName: string
): string {
  if (locationType === 'globalRegion') {
    return `Global ${locationName} eSIM | ${customerName}`;
  }
  if (locationType === 'region') {
    return `${locationName} Regional eSIM | ${customerName}`;
  }
  // SEO Best Practice: Primary keyword (country name) first, brand name last
  // Format: "{Country} eSIM Plans | {Brand}" - keeps it concise and keyword-focused
  return `${locationName} eSIM Plans | ${customerName}`;
}

/**
 * Generates default description based on location type
 */
function generateDefaultDescription(
  locationName: string,
  locationType: string,
  planCount: number,
  countryCount: number,
  minPrice: number,
  currency: string
): string {
  if (locationType === 'globalRegion') {
    return generateGlobalRegionDescription(
      locationName,
      planCount,
      countryCount,
      minPrice,
      currency
    );
  }

  if (locationType === 'region') {
    return generateRegionDescription(locationName, planCount, countryCount, minPrice, currency);
  }

  return generateCountryDescription(locationName, planCount, minPrice, currency);
}

/**
 * Generates description for global region
 */
function generateGlobalRegionDescription(
  locationName: string,
  planCount: number,
  countryCount: number,
  minPrice: number,
  currency: string
): string {
  const baseDesc = `Stay connected globally with ${locationName} eSIM. Coverage in ${countryCount} countries`;
  const planInfo = planCount > 0 ? `, ${planCount} plans available` : '';
  return formatDescriptionWithPlanInfo(baseDesc + planInfo, planCount, minPrice, currency);
}

/**
 * Generates description for region
 */
function generateRegionDescription(
  locationName: string,
  planCount: number,
  countryCount: number,
  minPrice: number,
  currency: string
): string {
  const baseDesc = `Stay connected across ${countryCount} ${locationName} countries with one eSIM. ${planCount} plans available`;
  return formatDescriptionWithPlanInfo(baseDesc, planCount, minPrice, currency);
}

/**
 * Generates description for country
 */
function generateCountryDescription(
  locationName: string,
  planCount: number,
  minPrice: number,
  currency: string
): string {
  const baseDesc = `Get affordable eSIM for ${locationName}. ${planCount} plans available`;
  return (
    formatDescriptionWithPlanInfo(baseDesc, planCount, minPrice, currency) +
    '. Instant activation, no SIM card needed.'
  );
}

/**
 * Generates default keywords based on location type
 */
function generateDefaultKeywords(
  locationName: string,
  locationType: string,
  customerName: string
): string {
  const keywordMap: Record<string, string> = {
    globalRegion: `global eSIM, ${locationName} eSIM, worldwide data, ${customerName}`,
    region: `${locationName} eSIM, regional eSIM, multi-country eSIM, ${customerName}`,
    country: `${locationName} eSIM, ${locationName} data plan, travel eSIM ${locationName}, ${customerName}`,
  };

  return keywordMap[locationType] || keywordMap['country'];
}

/**
 * Generates OG title
 */
function generateOgTitle(locationName: string, locationType: string, customerName: string): string {
  const titleMap: Record<string, string> = {
    globalRegion: `Global ${locationName} eSIM - ${customerName}`,
    region: `${locationName} Regional eSIM - ${customerName}`,
    country: `${locationName} eSIM Plans - ${customerName}`,
  };

  return titleMap[locationType] || titleMap['country'];
}

/**
 * Generates OG description
 */
function generateOgDescription(
  locationName: string,
  locationType: string,
  planCount: number,
  countryCount: number
): string {
  if (locationType === 'globalRegion') {
    return `Worldwide coverage in ${countryCount} countries`;
  }

  if (locationType === 'region') {
    return `One eSIM for ${countryCount} countries in ${locationName}`;
  }

  return `${planCount} affordable eSIM plans for ${locationName}`;
}

/**
 * Generates Twitter title
 */
function generateTwitterTitle(locationName: string, locationType: string): string {
  const titleMap: Record<string, string> = {
    globalRegion: `Global ${locationName} eSIM`,
    region: `${locationName} Regional eSIM`,
    country: `${locationName} eSIM Plans`,
  };

  return titleMap[locationType] || titleMap['country'];
}

/**
 * Generates Twitter description
 */
function generateTwitterDescription(
  planCount: number,
  countryCount: number,
  minPrice: number,
  currency: string,
  locationName: string
): string {
  if (countryCount > 0) {
    return `${countryCount} countries, ${planCount} plans`;
  }

  if (planCount > 0) {
    const priceText = minPrice > 0 ? ` from ${formatPrice(minPrice, currency)}` : '';
    return `${planCount} plans${priceText}`;
  }

  return `Affordable eSIM plans for ${locationName}`;
}

/**
 * Generates Product schema markup for location pages
 */
function generateProductSchema(
  locationName: string,
  locationType: string,
  customerName: string,
  countryCount: number,
  planCount: number,
  minPrice: number,
  currency: string
): SchemaMarkup {
  const productName = getProductName(locationName, locationType);
  const productDescription = getProductDescription(locationName, locationType, countryCount);
  const offers = createProductOffers(planCount, minPrice, currency);

  const schemaMarkup: SchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    description: productDescription,
    brand: {
      '@type': 'Brand',
      name: customerName,
    },
  };

  if (offers) {
    schemaMarkup.offers = offers;
  }

  return schemaMarkup;
}

/**
 * Gets product name for schema markup
 */
function getProductName(locationName: string, locationType: string): string {
  const nameMap: Record<string, string> = {
    globalRegion: `Global ${locationName} eSIM`,
    region: `${locationName} Regional eSIM`,
    country: `${locationName} eSIM`,
  };

  return nameMap[locationType] || nameMap['country'];
}

/**
 * Gets product description for structured data
 */
function getProductDescription(
  locationName: string,
  locationType: string,
  countryCount: number
): string {
  const descriptionMap: Record<string, string> = {
    globalRegion: `Global eSIM with coverage in ${countryCount} countries`,
    region: `Regional eSIM for ${locationName} covering ${countryCount} countries`,
    country: `eSIM data plans for ${locationName}`,
  };

  return descriptionMap[locationType] || descriptionMap['country'];
}

/**
 * Generates SEO metadata for a country page
 */
export function generateCountrySeo(
  cacheContent: CachedContent,
  countryId: string,
  template?: Partial<SeoMeta>
): Partial<SeoMeta> {
  const config = extractSeoConfig(cacheContent);

  const country = cacheContent.cache.singleCountries.find((c: Country) => c._id === countryId);
  if (!country) {
    return {};
  }

  return generateLocationSeo(config, country, 'country', template);
}

/**
 * Generates SEO metadata for a region page
 */
export function generateRegionSeo(
  cacheContent: CachedContent,
  regionId: string,
  template?: Partial<SeoMeta>
): Partial<SeoMeta> {
  const config = extractSeoConfig(cacheContent);

  const region = cacheContent.cache.regions?.find((r: Region) => r._id === regionId);
  if (!region) {
    return {};
  }

  return generateLocationSeo(config, region, 'region', template);
}

/**
 * Generates SEO metadata for a global region page
 */
export function generateGlobalRegionSeo(
  cacheContent: CachedContent,
  globalRegionId: string,
  template?: Partial<SeoMeta>
): Partial<SeoMeta> {
  const config = extractSeoConfig(cacheContent);

  const globalRegion = cacheContent.cache.globalRegions?.find(
    (gr: GlobalRegion) => gr._id === globalRegionId
  );
  if (!globalRegion) {
    return {};
  }

  return generateLocationSeo(config, globalRegion, 'globalRegion', template);
}
